// src/adapter/config-service-adapter.ts
import { composeSettingsView, UNSET_SOURCE_LEVEL } from "@hopdrive/sdk-configs";

// src/adapter/documents.ts
var GET_EDITABLE_SETTINGS = `
  query GetEditableSettings($level: String!, $levelId: bigint) {
    getEditableSettings(level: $level, levelId: $levelId) {
      level
      levelId
      settings {
        key
        label
        description
        warning
        category
        kind
        jsonSchema
        currentValue
        effectiveValue
        sourceLevel
        deprecated
      }
    }
  }
`;
var GET_EFFECTIVE_CONFIG = `
  query GetEffectiveConfig($level: String!, $levelId: bigint, $keys: [String!], $includeMetadata: Boolean) {
    getEffectiveConfig(level: $level, levelId: $levelId, keys: $keys, includeMetadata: $includeMetadata) {
      level
      levelId
      config
      metadata
    }
  }
`;
var UPDATE_CONFIG_FIELD = `
  mutation UpdateConfigField($level: String!, $levelId: bigint, $key: String!, $changes: jsonb!) {
    updateConfigField(level: $level, levelId: $levelId, key: $key, changes: $changes) {
      level
      levelId
      key
      storedValue
      effectiveValue
    }
  }
`;
var CLEAR_CONFIG_OVERRIDE = `
  mutation ClearConfigOverride($level: String!, $levelId: bigint, $key: String!) {
    clearConfigOverride(level: $level, levelId: $levelId, key: $key) {
      level
      levelId
      key
      cleared
      effectiveValue
    }
  }
`;

// src/adapter/errors.ts
var CONFIG_SERVICE_ERROR_CODES = [
  "unauthenticated",
  "unauthorized",
  "unknown_definition",
  "invalid_level",
  "not_writable_at_level",
  "schema_validation_failed",
  "target_not_found",
  "persistence_failed",
  "internal_error"
];
var ConfigServiceError = class extends Error {
  constructor(params) {
    super(params.message);
    this.name = "ConfigServiceError";
    this.code = params.code;
    this.rawCode = params.rawCode;
    this.operation = params.operation;
    this.issues = params.issues ?? null;
    this.cause = params.cause;
  }
};
function isRecord(value) {
  return typeof value === "object" && value !== null;
}
function findGraphQLError(thrown) {
  if (!isRecord(thrown)) return null;
  const arrays = [thrown.graphQLErrors, thrown.errors, isRecord(thrown.response) ? thrown.response.errors : void 0];
  for (const candidate of arrays) {
    if (Array.isArray(candidate) && candidate.length > 0 && isRecord(candidate[0])) {
      return candidate[0];
    }
  }
  if (isRecord(thrown.extensions)) return thrown;
  return null;
}
function isKnownCode(value) {
  return typeof value === "string" && CONFIG_SERVICE_ERROR_CODES.includes(value);
}
function messageOf(thrown, fallback) {
  if (thrown instanceof Error && thrown.message) return thrown.message;
  if (isRecord(thrown) && typeof thrown.message === "string" && thrown.message) return thrown.message;
  return fallback;
}
function toConfigServiceError(thrown, operation) {
  if (thrown instanceof ConfigServiceError) return thrown;
  const graphQLError = findGraphQLError(thrown);
  const extensions = graphQLError && isRecord(graphQLError.extensions) ? graphQLError.extensions : null;
  const rawCode = extensions && typeof extensions.code === "string" ? extensions.code : null;
  const issues = extensions && Array.isArray(extensions.issues) ? extensions.issues : null;
  const graphQLMessage = graphQLError && typeof graphQLError.message === "string" && graphQLError.message ? graphQLError.message : null;
  return new ConfigServiceError({
    message: graphQLMessage ?? messageOf(thrown, `${operation} failed`),
    code: isKnownCode(rawCode) ? rawCode : "internal_error",
    rawCode,
    operation,
    issues,
    cause: thrown
  });
}

// src/adapter/config-service-adapter.ts
function isRecord2(value) {
  return typeof value === "object" && value !== null;
}
function targetVariables(target) {
  return { level: target.level, levelId: target.levelId };
}
function byKey(a, b) {
  return a.key.localeCompare(b.key);
}
function createConfigServiceAdapter(client, options = {}) {
  const { role } = options;
  async function call(document, variables, operation) {
    let raw;
    try {
      raw = await client.request(document, variables);
    } catch (thrown) {
      throw toConfigServiceError(thrown, operation);
    }
    let payload = raw;
    if (isRecord2(raw) && ("errors" in raw || "data" in raw)) {
      if (Array.isArray(raw.errors) && raw.errors.length > 0) throw toConfigServiceError(raw, operation);
      payload = raw.data;
    }
    const field = isRecord2(payload) ? payload[operation] : void 0;
    if (field === void 0 || field === null) {
      throw new ConfigServiceError({
        message: `${operation} returned no data`,
        code: "internal_error",
        rawCode: null,
        operation,
        cause: raw
      });
    }
    return field;
  }
  function readMetadata(metadata, level) {
    const record = isRecord2(metadata) ? metadata : {};
    const sourceLevels = isRecord2(record.sourceLevels) ? record.sourceLevels : {};
    const levels = isRecord2(record.levels) ? record.levels : {};
    const storedAtLevel = isRecord2(levels[level]) ? levels[level] : null;
    return { sourceLevels, storedAtLevel };
  }
  return {
    async loadSettingsView(target) {
      const editableOutput = await call(
        GET_EDITABLE_SETTINGS,
        targetVariables(target),
        "getEditableSettings"
      );
      const serviceRows = Array.isArray(editableOutput.settings) ? editableOutput.settings : [];
      const editable = serviceRows.map((setting) => ({
        ...setting,
        access: "editable"
      }));
      if (!role) return [...editable].sort(byKey);
      const effective = await call(
        GET_EFFECTIVE_CONFIG,
        { ...targetVariables(target), keys: null, includeMetadata: true },
        "getEffectiveConfig"
      );
      const { sourceLevels, storedAtLevel } = readMetadata(effective.metadata, target.level);
      const composed = composeSettingsView({
        role,
        level: target.level,
        effectiveConfig: isRecord2(effective.config) ? effective.config : {},
        sourceLevels,
        storedAtLevel
      });
      const editableKeys = new Set(editable.map((row) => row.key));
      const readOnly = composed.filter((row) => !editableKeys.has(row.key)).map((row) => ({ ...row, access: "readonly" }));
      return [...editable, ...readOnly].sort(byKey);
    },
    async saveField(target, key, changes) {
      const output = await call(
        UPDATE_CONFIG_FIELD,
        { ...targetVariables(target), key, changes },
        "updateConfigField"
      );
      return {
        key: output.key ?? key,
        storedValue: output.storedValue ?? null,
        effectiveValue: output.effectiveValue ?? null,
        // ConfigUpdateOutput carries no sourceLevel (ADR 2.4), and it does not need to: the write
        // just stored the value AT the target, so the target level is where the effective value
        // now comes from. That stays true in the section 13 case where storedValue comes back
        // null because the caller may not read the key back.
        sourceLevel: target.level
      };
    },
    async clearOverride(target, key) {
      const output = await call(
        CLEAR_CONFIG_OVERRIDE,
        { ...targetVariables(target), key },
        "clearConfigOverride"
      );
      let sourceLevel = UNSET_SOURCE_LEVEL;
      try {
        const effective = await call(
          GET_EFFECTIVE_CONFIG,
          { ...targetVariables(target), keys: [key], includeMetadata: true },
          "getEffectiveConfig"
        );
        const { sourceLevels } = readMetadata(effective.metadata, target.level);
        if (typeof sourceLevels[key] === "string") sourceLevel = sourceLevels[key];
      } catch {
      }
      return {
        key: output.key ?? key,
        storedValue: null,
        effectiveValue: output.effectiveValue ?? null,
        sourceLevel
      };
    }
  };
}
export {
  CLEAR_CONFIG_OVERRIDE,
  CONFIG_SERVICE_ERROR_CODES,
  ConfigServiceError,
  GET_EDITABLE_SETTINGS,
  GET_EFFECTIVE_CONFIG,
  UPDATE_CONFIG_FIELD,
  createConfigServiceAdapter
};
//# sourceMappingURL=adapter.mjs.map